import React, { useState } from 'react';
import { Mail, Send, Users, Eye, CheckCircle2, AlertTriangle } from 'lucide-react';
import { adminAPI } from '../services/api';

/**
 * Admin → Email Tools
 * ───────────────────
 * Two jobs, both requiring a human in the loop:
 *   1. Send a SAMPLE welcome to any address (proofreading). Touches no user.
 *   2. Backfill the welcome to existing users — dry-run first (shows exactly
 *      who would get it), then an explicit confirmed send.
 *
 * Self-contained (own state + styling) so it can't destabilise the main
 * dashboard. `getToken` is the auth-token getter from useAuth().
 */
const card = 'bg-[#111827] border border-white/6 rounded-xl';
const label = 'text-[11px] font-semibold text-zinc-400 uppercase tracking-wide';
const input =
  'w-full bg-[#0b1220] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 ' +
  'placeholder-zinc-600 focus:outline-none focus:border-blue-500/60';

const AdminEmailTools = ({ getToken }) => {
  // ── sample ──────────────────────────────────────────────
  const [sampleEmail, setSampleEmail] = useState('hafsatmoreni96@gmail.com');
  const [sampleRole,  setSampleRole]  = useState('host');
  const [sampleName,  setSampleName]  = useState('Sam');
  const [sampleBusy,  setSampleBusy]  = useState(false);
  const [sampleMsg,   setSampleMsg]   = useState(null); // {ok, text}

  const sendSample = async () => {
    setSampleBusy(true); setSampleMsg(null);
    try {
      const res = await adminAPI.sendWelcomePreview(
        { email: sampleEmail.trim(), role: sampleRole, firstName: sampleName.trim() || 'Sam' },
        getToken()
      );
      setSampleMsg({ ok: true, text: `Sample (${res.variant}) sent to ${res.sentTo}.` });
    } catch (err) {
      setSampleMsg({ ok: false, text: err?.response?.data?.detail || 'Failed to send sample.' });
    } finally {
      setSampleBusy(false);
    }
  };

  // ── backfill ────────────────────────────────────────────
  const [dryBusy,  setDryBusy]  = useState(false);
  const [dryData,  setDryData]  = useState(null); // {wouldSend, skippedDemo, recipients:[]}
  const [sendBusy, setSendBusy] = useState(false);
  const [sendMsg,  setSendMsg]  = useState(null); // {ok, text}

  const runDryRun = async () => {
    setDryBusy(true); setSendMsg(null); setDryData(null);
    try {
      const res = await adminAPI.backfillWelcome(true, getToken());
      setDryData(res);
    } catch (err) {
      setSendMsg({ ok: false, text: err?.response?.data?.detail || 'Dry run failed.' });
    } finally {
      setDryBusy(false);
    }
  };

  const runSend = async () => {
    if (!dryData?.wouldSend) return;
    const ok = window.confirm(
      `Send the welcome email to ${dryData.wouldSend} existing user(s)? This cannot be undone.`
    );
    if (!ok) return;
    setSendBusy(true); setSendMsg(null);
    try {
      const res = await adminAPI.backfillWelcome(false, getToken());
      const failed = res.failed?.length || 0;
      setSendMsg({
        ok: failed === 0,
        text: `Sent ${res.sent} welcome email(s)` + (failed ? `, ${failed} failed.` : '.'),
      });
      setDryData(null); // list consumed
    } catch (err) {
      setSendMsg({ ok: false, text: err?.response?.data?.detail || 'Send failed.' });
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Mail size={16} className="text-blue-400" />
        <div>
          <div className="text-sm font-bold text-zinc-100">Email Tools</div>
          <div className="text-[11px] text-zinc-500">Welcome email — proofread a sample, then send to existing users.</div>
        </div>
      </div>

      {/* ── Send a sample ── */}
      <div className={`${card} p-4 space-y-3`}>
        <div className="flex items-center gap-2 text-xs font-bold text-zinc-200">
          <Eye size={13} className="text-blue-400" /> Send a sample (proofread)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <div className={label}>To email</div>
            <input className={input} value={sampleEmail} onChange={e => setSampleEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div>
            <div className={label}>First name</div>
            <input className={input} value={sampleName} onChange={e => setSampleName(e.target.value)} placeholder="Sam" />
          </div>
          <div>
            <div className={label}>Variant</div>
            <select className={input} value={sampleRole} onChange={e => setSampleRole(e.target.value)}>
              <option value="host">Host</option>
              <option value="guest">Guest</option>
            </select>
          </div>
        </div>
        <button
          onClick={sendSample}
          disabled={sampleBusy || !sampleEmail.trim()}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-lg"
        >
          <Send size={13} /> {sampleBusy ? 'Sending…' : 'Send sample'}
        </button>
        {sampleMsg && (
          <div className={`flex items-center gap-2 text-xs ${sampleMsg.ok ? 'text-emerald-400' : 'text-red-400'}`}>
            {sampleMsg.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {sampleMsg.text}
          </div>
        )}
      </div>

      {/* ── Backfill existing users ── */}
      <div className={`${card} p-4 space-y-3`}>
        <div className="flex items-center gap-2 text-xs font-bold text-zinc-200">
          <Users size={13} className="text-amber-400" /> Welcome existing users
        </div>
        <p className="text-[11px] text-zinc-500 leading-relaxed">
          One-time send to real users who never got a welcome. Skips demo accounts, admins,
          opted-out users, and anyone already welcomed. Always preview first.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runDryRun}
            disabled={dryBusy}
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 text-zinc-200 text-xs font-semibold px-4 py-2 rounded-lg"
          >
            <Eye size={13} /> {dryBusy ? 'Checking…' : 'Preview recipients'}
          </button>
          {dryData && dryData.wouldSend > 0 && (
            <button
              onClick={runSend}
              disabled={sendBusy}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-lg"
            >
              <Send size={13} /> {sendBusy ? 'Sending…' : `Send to ${dryData.wouldSend} user(s)`}
            </button>
          )}
        </div>

        {dryData && (
          <div className="bg-[#0b1220] border border-white/6 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-white/6 text-[11px] text-zinc-400 flex items-center gap-3">
              <span><b className="text-zinc-200">{dryData.wouldSend}</b> would receive it</span>
              <span className="text-zinc-600">·</span>
              <span>{dryData.skippedDemo} demo skipped</span>
            </div>
            {dryData.recipients?.length ? (
              <div className="max-h-56 overflow-y-auto divide-y divide-white/5">
                {dryData.recipients.map(r => (
                  <div key={r.id} className="px-3 py-2 flex items-center justify-between text-xs">
                    <div>
                      <div className="text-zinc-200">{r.name || <span className="text-zinc-600 italic">no name</span>}</div>
                      <div className="text-zinc-500 font-mono text-[10px]">{r.email}</div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${r.variant === 'host' ? 'bg-blue-500/15 text-blue-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                      {r.variant}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">No eligible users — everyone's already been welcomed.</div>
            )}
          </div>
        )}

        {sendMsg && (
          <div className={`flex items-center gap-2 text-xs ${sendMsg.ok ? 'text-emerald-400' : 'text-red-400'}`}>
            {sendMsg.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {sendMsg.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailTools;
